import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/api';

// Mirrors the Scale portal's Account → Files tab: two categories of mediator
// documents live on the shared `mediator_upload_files` table. Agreements are
// attached to client booking emails; W-9s keep payouts on schedule.
//
// Per-category rules match Scale's Files tab EXACTLY (FilesTab.jsx):
//   - Agreement: pdf,doc,docx,jpg,jpeg,png · 30MB · up to 5 files
//   - W-9 Form:  PDF only                  · 10MB · up to 3 files
const FILE_TYPES = [
  {
    value: 'agreement_form',
    label: 'Agreement',
    section: 'Agreements',
    subtitle: "Signed agreements that don't need a signature request — attached to client booking emails.",
    accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png',
    exts: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
    acceptLabel: 'PDF, DOC, DOCX, JPG, PNG',
    maxFiles: 5,
    maxSizeMB: 30,
  },
  {
    value: 'w9_form',
    label: 'W-9 Form',
    section: 'W-9 Forms',
    subtitle: 'The most recent W-9 is used for payouts. PDF only.',
    accept: '.pdf',
    exts: ['pdf'],
    acceptLabel: 'PDF only',
    maxFiles: 3,
    maxSizeMB: 10,
  },
];

const ruleFor = (value) => FILE_TYPES.find((t) => t.value === value) || FILE_TYPES[0];

const cardStyle = { background: '#fff', border: '1px solid #e5e5e5', borderRadius: 10 };

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function typeLabel(value) {
  return FILE_TYPES.find((t) => t.value === value)?.label || value || 'File';
}

export default function DocumentsTab({ mediatorId, notify }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [confirmFile, setConfirmFile] = useState(null);
  const [fileType, setFileType] = useState('agreement_form');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const rule = ruleFor(fileType);

  const fetchFiles = useCallback(async () => {
    if (!mediatorId) {
      setFiles([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(`/mediators/${mediatorId}/files`);
      const data = res.data?.data || [];
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to load documents' });
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [mediatorId, notify]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUpload = useCallback(
    async (selected) => {
      const list = Array.from(selected || []).filter(Boolean);
      if (!list.length) return;
      const r = ruleFor(fileType);
      if (list.length > r.maxFiles) {
        notify?.({ type: 'error', message: `Up to ${r.maxFiles} ${r.label} file(s) per upload.` });
        return;
      }
      const badExt = list.find((f) => {
        const ext = (f.name.split('.').pop() || '').toLowerCase();
        return !r.exts.includes(ext);
      });
      if (badExt) {
        notify?.({ type: 'error', message: `"${badExt.name}" is not allowed for ${r.label}. Accepted: ${r.acceptLabel}.` });
        return;
      }
      const tooBig = list.find((f) => f.size > r.maxSizeMB * 1024 * 1024);
      if (tooBig) {
        notify?.({ type: 'error', message: `"${tooBig.name}" exceeds the ${r.maxSizeMB}MB limit.` });
        return;
      }
      const form = new FormData();
      list.forEach((f) => form.append('files', f));
      form.append('file_type', fileType);
      try {
        setUploading(true);
        await api.post(`/mediators/${mediatorId}/files`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        notify?.({ type: 'success', message: 'Document(s) uploaded' });
        await fetchFiles();
      } catch (err) {
        notify?.({ type: 'error', message: err.response?.data?.message || 'Upload failed. Please try again.' });
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [mediatorId, fileType, notify, fetchFiles]
  );

  // Force an actual file download (Scale parity — FilesTab fetches the object
  // as a blob and saves it with its original name, instead of opening a tab).
  // Falls back to opening the URL if the fetch is blocked (e.g. CORS).
  const handleDownload = useCallback(
    async (file) => {
      if (!file?.file_url) return;
      try {
        setDownloadingId(file.id);
        const res = await fetch(file.file_url);
        if (!res.ok) throw new Error('fetch failed');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.file_name || 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        // Fallback: let the browser handle it in a new tab.
        window.open(file.file_url, '_blank', 'noopener,noreferrer');
      } finally {
        setDownloadingId(null);
      }
    },
    []
  );

  const handleDelete = useCallback(
    async (file) => {
      if (!file?.id) return;
      try {
        setDeletingId(file.id);
        await api.delete(`/mediators/${mediatorId}/files/${file.id}`);
        setFiles((prev) => prev.filter((f) => f.id !== file.id));
        notify?.({ type: 'success', message: 'Document deleted' });
      } catch (err) {
        notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to delete document' });
      } finally {
        setDeletingId(null);
        setConfirmFile(null);
      }
    },
    [mediatorId, notify]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (uploading) return;
    handleUpload(e.dataTransfer?.files);
  };

  // Group files by category (files arrive created_at desc from the BE). Within
  // the W-9 group the first (newest) row is the "Active" one used for payouts.
  const grouped = useMemo(() => {
    return FILE_TYPES.map((t) => ({
      ...t,
      rows: files.filter((f) => f.file_type === t.value),
    }));
  }, [files]);

  const activeW9Id = useMemo(
    () => files.find((f) => f.file_type === 'w9_form')?.id ?? null,
    [files]
  );

  return (
    <div className="flex flex-col gap-5">
      {/* Upload card */}
      <div style={cardStyle}>
        <div className="px-5 pt-4 pb-3 border-b border-[#e5e5e5] flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-[#0e1e20] text-sm font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Documents
            </h3>
            <p className="text-[#5b5a64] text-[12px] mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
              Upload agreements and W-9 forms for this mediator.
            </p>
          </div>
          <div className="inline-flex items-center rounded-lg p-0.5" style={{ background: '#f3f4f6' }}>
            {FILE_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setFileType(t.value)}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  background: fileType === t.value ? '#fff' : 'transparent',
                  color: fileType === t.value ? '#115e59' : '#5b5a64',
                  boxShadow: fileType === t.value ? '0 1px 2px rgba(16,24,40,0.08)' : 'none',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-5">
          <div
            role="button"
            tabIndex={0}
            onClick={() => !uploading && inputRef.current?.click()}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !uploading) {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className="flex flex-col items-center justify-center text-center"
            style={{
              border: `1.5px dashed ${dragging ? '#115e59' : '#d1d5db'}`,
              borderRadius: 10,
              background: dragging ? '#f0fdfa' : '#fafafa',
              padding: '32px 20px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" style={{ marginBottom: 10, opacity: 0.7 }}>
              <path d="M12 16V4m0 0L7 9m5-5 5 5" stroke="#115e59" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" stroke="#115e59" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <span className="text-[13px] font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter, sans-serif' }}>
              {uploading ? 'Uploading…' : (
                <>Drop files here, or <span style={{ color: '#115e59' }}>click to browse</span></>
              )}
            </span>
            <span className="text-[11px] text-[#9ca3af] mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
              Uploading as {typeLabel(fileType)} · {rule.acceptLabel} · up to {rule.maxFiles} files · max {rule.maxSizeMB}MB each
            </span>
            <input
              ref={inputRef}
              type="file"
              accept={rule.accept}
              multiple
              hidden
              disabled={uploading}
              onChange={(e) => handleUpload(e.target.files)}
            />
          </div>
        </div>
      </div>

      {/* File list — grouped by category (Scale parity) */}
      {loading ? (
        <div style={cardStyle} className="flex items-center justify-center" >
          <div className="w-6 h-6 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" style={{ margin: '40px 0' }} />
        </div>
      ) : (
        grouped.map((group) => (
          <div key={group.value} style={cardStyle}>
            <div className="px-5 pt-4 pb-3 border-b border-[#e5e5e5]">
              <h4 className="text-[#0e1e20] text-[13px] font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                {group.section}
              </h4>
              <p className="text-[#5b5a64] text-[11.5px] mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                {group.subtitle}
              </p>
            </div>

            {group.rows.length === 0 ? (
              <div className="text-center" style={{ padding: '28px 20px' }}>
                <span className="text-[13px] text-[#9ca3af]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  No {group.label.toLowerCase()} files uploaded yet.
                </span>
              </div>
            ) : (
              <div>
                <div
                  className="flex items-center border-b border-[#e5e5e5]"
                  style={{ padding: '10px 20px', background: '#fafafa' }}
                >
                  <span className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]" style={{ fontFamily: 'Inter, sans-serif' }}>File name</span>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]" style={{ width: 130, fontFamily: 'Inter, sans-serif' }}>Uploaded</span>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af] text-right" style={{ width: 110, fontFamily: 'Inter, sans-serif' }}>Actions</span>
                </div>
                {group.rows.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center border-b border-[#f0f0f0] last:border-b-0"
                    style={{ padding: '12px 20px' }}
                  >
                    <span className="flex-1 flex items-center gap-2 min-w-0 pr-3">
                      <span className="text-[13px] text-[#0e1e20] truncate" style={{ fontFamily: 'Inter, sans-serif' }} title={file.file_name}>
                        {file.file_name || 'Untitled'}
                      </span>
                      {group.value === 'w9_form' && file.id === activeW9Id && (
                        <span
                          className="inline-flex items-center rounded-full text-[10px] font-semibold flex-shrink-0"
                          style={{ background: '#dcfce7', color: '#15803d', padding: '2px 8px', fontFamily: 'Inter, sans-serif' }}
                        >
                          Active
                        </span>
                      )}
                    </span>
                    <span className="text-[12px] text-[#5b5a64]" style={{ width: 130, fontFamily: 'Inter, sans-serif' }}>
                      {formatDate(file.created_at)}
                    </span>
                    <span className="flex items-center justify-end gap-2" style={{ width: 110 }}>
                      {file.file_url && (
                        <button
                          type="button"
                          onClick={() => handleDownload(file)}
                          disabled={downloadingId === file.id}
                          title="Download"
                          className="p-1.5 rounded-md text-[#115e59] hover:bg-[#f0fdfa] disabled:opacity-40"
                          style={{ border: 'none', background: 'transparent', cursor: downloadingId === file.id ? 'not-allowed' : 'pointer' }}
                        >
                          {downloadingId === file.id ? (
                            <div className="w-[15px] h-[15px] border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                              <path d="M12 4v11m0 0 4-4m-4 4-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setConfirmFile(file)}
                        disabled={deletingId === file.id}
                        title="Delete"
                        className="p-1.5 rounded-md text-[#dc2626] hover:bg-[#fef2f2] disabled:opacity-40"
                        style={{ border: 'none', background: 'transparent', cursor: deletingId === file.id ? 'not-allowed' : 'pointer' }}
                      >
                        {deletingId === file.id ? (
                          <div className="w-[15px] h-[15px] border-2 border-[#dc2626] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                            <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {/* Styled delete confirmation (Scale parity — replaces window.confirm) */}
      {confirmFile && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center"
          style={{ background: 'rgba(15,23,32,0.45)' }}
          onClick={() => deletingId == null && setConfirmFile(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl"
            style={{ width: 380, maxWidth: '90vw', padding: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-[#0e1e20] text-[15px] font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Delete document
            </h4>
            <p className="text-[#5b5a64] text-[13px] mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              Delete “{confirmFile.file_name || 'this file'}”? This cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirmFile(null)}
                disabled={deletingId != null}
                className="rounded-lg text-[13px] font-semibold disabled:opacity-40"
                style={{ padding: '8px 14px', border: '1px solid #e5e5e5', background: '#fff', color: '#0e1e20', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(confirmFile)}
                disabled={deletingId != null}
                className="rounded-lg text-[13px] font-semibold text-white disabled:opacity-60 inline-flex items-center gap-2"
                style={{ padding: '8px 16px', border: 'none', background: '#dc2626', cursor: deletingId != null ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif' }}
              >
                {deletingId != null && (
                  <div className="w-[13px] h-[13px] border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
